"use client";

import React from "react";
import Link from "next/link";
import { Button, CircularProgress, Icon, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { LoadingButton } from "@mui/lab";

import { CartPricingMode, SVariantDto } from "@/__generated__/sdk";
import { useCartItem, useCartPricingMode } from "@/redux/features/cart/hooks";
import { useBreakPoints } from "@/lib/useBreakPoints";

import ShoppingBagIcon from "@/app/_components/Icons/ShoppingBagIcon";
import TrashIcon from "@/app/_components/Icons/TrashIcon";
import AddIcon from "@/app/_components/Icons/AddIcon";
import MinusIcon from "@/app/_components/Icons/MinusIcon";

type Props = {
  variant?: SVariantDto | null;
  productId: string;
  dontShowCartButton?: boolean;
  handleQuantityChange: (quantity: number, pricingMode?: CartPricingMode) => void;
  isPending: boolean;
  layout?: "default" | "dense" | "buybox";
  /** When set, the button belongs to a CASH/CREDIT seller row and adds the item in that mode. */
  pricingMode?: CartPricingMode;
  disabled?: boolean;
};

export default function ProductVariantAddToCartSection({
  variant,
  productId: _productId,
  dontShowCartButton = false,
  handleQuantityChange,
  isPending,
  layout = "default",
  pricingMode,
  disabled = false,
}: Props) {
  const { mdAndDown } = useBreakPoints();

  const cartPricingMode = useCartPricingMode();
  const variantCartItem = useCartItem(variant?.id);
  // A seller row only owns the cart line when the cart is in that row's mode
  const cartItem = variantCartItem && (!pricingMode || !cartPricingMode || cartPricingMode === pricingMode) ? variantCartItem : null;
  const quantity = cartItem?.quantity ?? 0;
  const maxBasketSize = variant?.offer?.maxBasketCount ?? variant?.maxBasketCount ?? 0;
  const isAvailable = Boolean(variant?.hasStock);
  const disableAdd = Boolean(cartItem && quantity >= maxBasketSize);
  const isDense = layout === "dense";
  const isBuyBox = layout === "buybox";
  const controlHeight = isBuyBox ? 48 : isDense ? 40 : 44;
  const controlRadius = isBuyBox ? 3 : 500;

  const quantityControls = cartItem && (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={0.25}
      sx={{
        flexShrink: 0,
        height: controlHeight,
        p: 0.25,
        borderRadius: isBuyBox ? controlRadius : 2,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Button
        variant="contained"
        disableElevation
        aria-label="افزایش تعداد"
        disabled={disableAdd || isPending || disabled}
        onClick={() => handleQuantityChange(quantity + 1, pricingMode)}
        sx={{
          minWidth: "auto",
          width: 32,
          height: 32,
          p: 0,
          borderRadius: isBuyBox ? 2 : 1.5,
          "&.Mui-disabled": { bgcolor: "grey.100", color: "grey.400" },
        }}
      >
        <Icon fontSize="small">
          <AddIcon />
        </Icon>
      </Button>

      <Box
        sx={{
          minWidth: isDense ? 28 : 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isPending ? (
          <CircularProgress size={14} />
        ) : (
          <Typography
            variant="body2"
            fontWeight={800}
            color="grey.900"
            sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1 }}
          >
            {quantity.toLocaleString("fa-IR")}
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        disableElevation
        aria-label={quantity === 1 ? "حذف از سبد" : "کاهش تعداد"}
        disabled={isPending || disabled}
        onClick={() => handleQuantityChange(quantity - 1, pricingMode)}
        sx={{
          minWidth: "auto",
          width: 32,
          height: 32,
          p: 0,
          borderRadius: isBuyBox ? 2 : 1.5,
        }}
      >
        <Icon fontSize="small">{quantity === 1 ? <TrashIcon /> : <MinusIcon />}</Icon>
      </Button>
    </Box>
  );

  if (cartItem) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={isDense ? 0.75 : 1.5}
        sx={{
          mt: isDense ? 0 : "4px !important",
          width: "100%",
          flexWrap: "nowrap",
        }}
      >
        {!dontShowCartButton && (
          <Button
            component={Link}
            href="/cart"
            variant="contained"
            disableElevation
            size={isDense ? "small" : mdAndDown ? "medium" : "large"}
            startIcon={<ShoppingBagIcon />}
            className="btn-shine"
            sx={{
              flex: 1,
              height: controlHeight,
              px: isDense ? 1.5 : 2,
              borderRadius: isDense ? 2 : controlRadius,
              backgroundColor: "grey.900",
              fontWeight: isBuyBox || isDense ? 800 : 600,
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {isDense ? "سبد خرید" : "مشاهده سبد خرید"}
          </Button>
        )}

        {quantityControls}
      </Stack>
    );
  }

  return (
    <LoadingButton
      fullWidth={!isDense}
      loading={isPending}
      disabled={!isAvailable || disabled}
      onClick={() => handleQuantityChange(1, pricingMode)}
      variant="contained"
      disableElevation
      size={isDense ? "small" : mdAndDown ? "medium" : "large"}
      startIcon={isAvailable ? <ShoppingBagIcon /> : undefined}
      sx={{
        mt: isDense || isBuyBox ? 0 : "4px !important",
        height: controlHeight,
        px: isDense ? 1.5 : 2,
        borderRadius: isDense ? 2 : controlRadius,
        backgroundColor: "grey.900",
        fontWeight: isBuyBox || isDense ? 800 : 600,
        fontSize: isBuyBox ? "0.95rem" : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {isAvailable ? "افزودن به سبد خرید" : "ناموجود"}
    </LoadingButton>
  );
}
